/**
 * Stage H — Fire Protection panel.
 *   H.1  Fire Water System   → H1FireWater
 *   H.2  F&G Detection        → H2Detection
 *   H.3  Suppression & Passive → H3Suppression
 *   H.4  Fire MTO & Deliverables → H4MTO
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

const _fmt = (n, d = 1) => (n == null ? "-"
    : typeof n === "number" ? n.toFixed(d) : n);

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3"
          data-testid={testid}>
      <div className="text-2xl font-bold text-neutral-100">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4"
          data-testid={testid}>
      <div className="mb-2 text-sm font-semibold text-neutral-200">{title}</div>
      {children}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded border border-neutral-800"
          data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-neutral-900 text-neutral-400">
          <tr>{cols.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-neutral-800 text-neutral-200">
              {cols.map(([k]) => <td key={k} className="px-2 py-1.5">{_fmt(r?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function _openSvg(projectId, path, payload) {
  const method = payload ? "post" : "get";
  const r = await api[method](`/projects/${projectId}${path}`, payload || undefined);
  const blob = new Blob([r.data], { type: "image/svg+xml" });
  window.open(URL.createObjectURL(blob), "_blank");
}

async function _downloadDxf(projectId, path, payload, filename) {
  const r = await api.post(`/projects/${projectId}${path}`, payload || {});
  const blob = new Blob([r.data], { type: "application/dxf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click(); URL.revokeObjectURL(url);
}

/* ═══════════════════ H.1 · Fire Water System ══════════════════ */
function H1FireWater({ projectId, res }) {
  const demand = res?.fire_water_demand || {};
  const gov = demand.governing_scenario || {};
  const storage = res?.storage || {};
  const pumps = res?.pumps || {};
  const hyd = res?.hydraulic || {};
  const std = res?.country_standards || {};
  const table = res?.fw_demand_table_first10 || [];
  return (
    <div className="space-y-4" data-testid="h1-fire-water">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Design demand (m³/h)"
                    value={_fmt(demand.design_demand_m3h, 0)}
                    testid="h1-tile-demand" />
        <StatTile label="Storage (m³)"
                    value={_fmt(storage.total_storage_m3, 0)}
                    testid="h1-tile-storage" />
        <StatTile label="Pump motor (kW)"
                    value={_fmt(pumps.motor_rating_kW, 0)}
                    testid="h1-tile-pumpkw" />
        <StatTile label="Residual (barg)"
                    value={_fmt(hyd.residual_pressure_barg, 1)}
                    testid="h1-tile-residual" />
      </div>

      <Section title={`Governing scenario · ${gov.type || "-"} on ${gov.affected || "-"}`}
                  testid="h1-governing">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300">
          <div>Wetted area (m²): <b>{_fmt(gov.wetted_area_m2)}</b></div>
          <div>Cooling (m³/h): <b>{_fmt((gov.cooling_hot_m3h || 0) + (gov.cooling_adj_m3h || 0))}</b></div>
          <div>Foam (m³/h): <b>{_fmt(gov.foam_m3h)}</b></div>
          <div>Hyd + Mon (m³/h): <b>{_fmt(gov.hydrant_monitor_m3h)}</b></div>
          <div>Code: <b>{gov.code_reference || "-"}</b></div>
        </div>
      </Section>

      <Section title="Fire water demand table (H.1-F7)" testid="h1-demand-table">
        <Table cols={[
          ["scenario", "Scenario"], ["equipment", "Equipment"],
          ["wetted_area_m2", "Wetted (m²)"], ["cooling_m3h", "Cooling m³/h"],
          ["foam_m3h", "Foam m³/h"], ["hydrant_m3h", "Hyd m³/h"],
          ["total_m3h", "Total m³/h"], ["governing", "Gov?"],
        ]} rows={table} />
      </Section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Section title="Storage sizing (H.1-F2)" testid="h1-storage-box">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Duration: <b>{storage.duration_hr} hr</b></div>
            <div>Working capacity: <b>{_fmt(storage.working_capacity_m3, 0)} m³</b></div>
            <div>Tanks: <b>{storage.n_tanks}</b> · Per tank: <b>{_fmt(storage.capacity_per_tank_m3, 0)} m³</b></div>
            <div>Tank type: <b>{storage.tank_type}</b></div>
            <div>Code: <b>{storage.code_reference}</b></div>
          </div>
        </Section>
        <Section title="Pump selection (H.1-F3)" testid="h1-pumps-box">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Electric main: <b>{_fmt(pumps.electric_main?.kW)} kW</b> · {pumps.electric_main?.type}</div>
            <div>Diesel standby: <b>{_fmt(pumps.diesel_standby?.kW)} kW</b> · {pumps.diesel_standby?.fuel_capacity_hr} hr fuel</div>
            <div>Jockey: <b>{_fmt(pumps.jockey?.kW)} kW</b> · {_fmt(pumps.jockey?.capacity_m3h)} m³/h</div>
            <div>Listing: <b>{pumps.electric_main?.listing}</b></div>
          </div>
        </Section>
      </div>

      <Section title="Hydraulic check (H.1-F4)" testid="h1-hydraulic">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-300">
          <div>Ring main: <b>{hyd.ring_main_length_m} m · DN{hyd.ring_main_dia_mm}</b></div>
          <div>Velocity: <b>{_fmt(hyd.velocity_ms, 2)} m/s</b> {hyd.velocity_ok ? "✓" : "⚠"}</div>
          <div>Head loss: <b>{_fmt(hyd.total_head_loss_m, 2)} m</b></div>
          <div>Hydrant check: <b className={hyd.hydrant_check?.status === "PASS" ? "text-green-400" : "text-red-400"}>{hyd.hydrant_check?.status}</b></div>
          <div>Monitor check: <b className={hyd.monitor_check?.status === "PASS" ? "text-green-400" : "text-red-400"}>{hyd.monitor_check?.status}</b></div>
          <div>Method: <b>{hyd.method}</b></div>
        </div>
      </Section>

      <Section title="Country standards (H.1-F8)" testid="h1-standards">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-300">
          <div>Country: <b>{res?.country}</b></div>
          <div>Primary: <b>{std.primary}</b></div>
          <div>Pump std: <b>{std.pump}</b></div>
          <div>Hydrant std: <b>{std.hydrant}</b></div>
          <div>Storage std: <b>{std.storage}</b></div>
          <div>Authority: <b>{std.authority}</b></div>
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="h1-layout-svg-btn"
                  onClick={() => _openSvg(projectId, "/stage-h/fw-layout/svg",
                    { plot_size_m: [100, 60],
                       hydrants: res?.hydrant_coords || [],
                       monitors: res?.monitor_coords || [],
                       country: res?.country || "IN" })}>
          Preview FW layout SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h1-layout-dxf-btn"
                  onClick={() => _downloadDxf(projectId, "/stage-h/fw-layout/dxf",
                    { plot_size_m: [100, 60],
                       hydrants: res?.hydrant_coords || [],
                       monitors: res?.monitor_coords || [],
                       country: res?.country || "IN" },
                    "fw-layout.dxf")}>
          Download FW layout DXF
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h1-pid-svg-btn"
                  onClick={() => _openSvg(projectId, "/stage-h/fw-pid/svg", {})}>
          Preview FW P&amp;ID SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h1-pid-dxf-btn"
                  onClick={() => _downloadDxf(projectId, "/stage-h/fw-pid/dxf", {},
                    "fw-pid.dxf")}>
          Download FW P&amp;ID DXF
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ H.2 · F&G Detection ═══════════════════════ */
function H2Detection({ projectId, res }) {
  const det = res?.detection || {};
  const zones = det.zones || [];
  const ce = res?.fg_ce_matrix || {};
  const count = res?.detector_count_summary || {};
  const ds = res?.detector_datasheets || [];
  return (
    <div className="space-y-4" data-testid="h2-detection">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Detection zones"    value={det.n_zones || 0}
                    testid="h2-tile-zones" />
        <StatTile label="Gas point detectors"    value={det.total_gas_point_detectors || 0}
                    testid="h2-tile-gas" />
        <StatTile label="Flame detectors"    value={det.total_flame_detectors || 0}
                    testid="h2-tile-flame" />
        <StatTile label="Field devices total"    value={count.total_field_devices || 0}
                    testid="h2-tile-devices" />
      </div>

      <Section title={`Zone mapping (${det.code_reference})`}
                  testid="h2-zones-table">
        <Table cols={[
          ["zone_id", "Zone"], ["zone_type", "Type"],
          ["area_context", "Area"], ["area_m2", "Area (m²)"],
          ["gas_point_detectors", "Gas Pt"],
          ["gas_open_path_detectors", "Gas Op"],
          ["gas_geographic_coverage_pct", "Gas cov %"],
          ["gas_coverage_status", "Gas ✓"],
          ["flame_detectors", "Flame"],
          ["flame_geographic_coverage_pct", "Flame cov %"],
          ["flame_coverage_status", "Fl ✓"],
          ["manual_call_points", "MCP"],
        ]} rows={zones} />
      </Section>

      <Section title={`F&G Cause & Effect matrix (${(ce.causes || []).length} causes × ${(ce.effects || []).length} effects)`}
                  testid="h2-ce-matrix">
        <div className="overflow-x-auto rounded border border-neutral-800">
          <table className="w-full text-[10px]">
            <thead className="bg-neutral-900 text-neutral-400">
              <tr>
                <th className="px-2 py-2 text-left font-medium">Cause \ Effect</th>
                {(ce.effects || []).map(e => (
                  <th key={e.tag} className="px-1 py-2 text-center font-medium"
                        title={e.description}>{e.tag}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(ce.causes || []).map(c => (
                <tr key={c.tag} className="border-t border-neutral-800 text-neutral-200">
                  <td className="px-2 py-1" title={c.description}>{c.tag}</td>
                  {(ce.effects || []).map(e => {
                    const v = ce.cells?.[`${c.tag}|${e.tag}`] || "";
                    const color = v === "X" ? "text-red-400" :
                                     v === "T" ? "text-amber-400" :
                                     v === "A" ? "text-blue-400" : "text-neutral-600";
                    return (
                      <td key={e.tag} className={`px-1 py-1 text-center font-mono ${color}`}>
                        {v || "·"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] text-neutral-500">
          Legend: <span className="text-red-400">X</span>=trip · <span className="text-amber-400">T</span>=time-delayed · <span className="text-blue-400">A</span>=alarm · Voting: gas {ce.voting?.gas_trip} / flame {ce.voting?.flame_trip}
        </div>
      </Section>

      <Section title={`Detector datasheets (${ds.length} types)`}
                  testid="h2-datasheets">
        <Table cols={[
          ["type", "Type"], ["measurement_principle", "Principle"],
          ["alarm_setpoint", "Alarm"], ["trip_setpoint", "Trip"],
          ["response_time_s", "T (s)"], ["SIL_rating", "SIL"],
          ["ex_rating", "Ex"], ["count", "Qty"],
        ]} rows={ds} />
      </Section>

      <Section title="Detector count summary (H.2-F5)"
                  testid="h2-count-summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300">
          {Object.entries(count).map(([k, v]) => (
            <div key={k}>{k.replace(/_/g, " ")}: <b>{String(v)}</b></div>
          ))}
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="h2-layout-svg-btn"
                  onClick={() => _openSvg(projectId, "/stage-h/fg-layout/svg",
                    { plot_size_m: [100, 60], detection: det,
                       country: res?.country || "IN" })}>
          Preview F&amp;G layout SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h2-layout-dxf-btn"
                  onClick={() => _downloadDxf(projectId, "/stage-h/fg-layout/dxf",
                    { plot_size_m: [100, 60], detection: det,
                       country: res?.country || "IN" }, "fg-layout.dxf")}>
          Download F&amp;G layout DXF
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h2-arch-svg-btn"
                  onClick={() => _openSvg(projectId, "/stage-h/fg-architecture/svg", {})}>
          Preview F&amp;G architecture SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h2-arch-dxf-btn"
                  onClick={() => _downloadDxf(projectId, "/stage-h/fg-architecture/dxf",
                    {}, "fg-architecture.dxf")}>
          Download F&amp;G architecture DXF
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ H.3 · Suppression ═════════════════════════ */
function H3Suppression({ projectId, res }) {
  const deluge = res?.deluge_designs_first5 || [];
  const foam = res?.foam_design || {};
  const fp = res?.fireproofing || {};
  const byArea = res?.fire_protection_by_area || [];
  const emrg = res?.emergency_facilities || {};
  return (
    <div className="space-y-4" data-testid="h3-suppression">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Deluge stations"
                    value={res?.deluge_designs_count || 0}
                    testid="h3-tile-deluge" />
        <StatTile label="Foam tanks covered"
                    value={foam.n_tanks || 0}
                    testid="h3-tile-foam" />
        <StatTile label="Fireproofing (m²)"
                    value={_fmt(fp.total_fireproofing_m2, 0)}
                    testid="h3-tile-fp" />
        <StatTile label="Safety showers"
                    value={emrg.safety_showers?.count || 0}
                    testid="h3-tile-showers" />
      </div>

      <Section title={`Deluge system design (first 5 of ${res?.deluge_designs_count})`}
                  testid="h3-deluge-table">
        <Table cols={[
          ["equipment_tag", "Equipment"],
          ["wetted_area_m2", "Wetted (m²)"],
          ["application_rate_L_min_m2", "Rate L/min/m²"],
          ["total_flow_L_min", "Flow (L/min)"],
          ["n_nozzles", "Nozzles"],
          ["deluge_valve_size", "Valve"],
          ["branch_pipe_dia_mm", "Branch (mm)"],
        ]} rows={deluge} />
      </Section>

      <Section title={`Foam system · ${foam.n_tanks} tanks · ${foam.application_rate_L_min_m2} L/min/m²`}
                  testid="h3-foam-table">
        <Table cols={[
          ["tag", "Tank"],
          ["diameter_m", "Ø (m)"],
          ["roof_area_m2", "Roof (m²)"],
          ["foam_flow_L_min", "Flow L/min"],
          ["n_pourers", "Pourers"],
          ["duration_min", "Dur (min)"],
          ["concentrate_volume_L", "Conc (L)"],
        ]} rows={foam.tank_designs || []} />
        <div className="mt-2 text-xs text-neutral-400">
          Total concentrate: <b>{_fmt(foam.total_concentrate_L, 0)} L</b> · Concentrate tank: <b>{_fmt(foam.concentrate_tank_L, 0)} L</b> · Proportioner: <b>{foam.proportioner?.size} {foam.proportioner?.type}</b>
        </div>
      </Section>

      <Section title={`Fireproofing · ${fp.fire_curve} · ${fp.target_rating_min}-min`}
                  testid="h3-fireproofing">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300 mb-2">
          <div>Columns: <b>{_fmt(fp.structural_column_fireproofing_m2, 0)} m²</b></div>
          <div>Beams: <b>{_fmt(fp.beam_fireproofing_m2, 0)} m²</b></div>
          <div>Vessel skirts: <b>{_fmt(fp.vessel_skirt_fireproofing_m2, 0)} m²</b></div>
          <div>Cable fire stops: <b>{fp.cable_tray_fire_stops}</b></div>
        </div>
        <Table cols={[
          ["type", "Material"], ["brand_ref", "Brand ref"],
          ["thickness_mm", "Thk (mm)"], ["rating_min", "Rating (min)"],
          ["area_m2", "Area (m²)"], ["usage", "Usage"],
        ]} rows={fp.materials || []} />
      </Section>

      <Section title="Fire protection by area (H.3-F4)" testid="h3-by-area">
        <Table cols={[
          ["area", "Area"], ["hazard_type", "Hazard"],
          ["area_m2", "Area (m²)"],
          ["demand_m3h_estimate", "Demand m³/h"],
        ]} rows={byArea} />
        <div className="mt-2 text-[11px] text-neutral-400 space-y-0.5">
          {byArea.map((a, i) => (
            <div key={i}><b>{a.area}:</b> {(a.systems || []).join(" · ")}</div>
          ))}
        </div>
      </Section>

      <Section title="Emergency response facilities (H.3-F5)" testid="h3-emergency">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-300">
          <div>Fire station: <b>{emrg.fire_station?.count} · {emrg.fire_station?.response_time_min} min</b></div>
          <div>Assembly points: <b>{emrg.assembly_points?.count}</b></div>
          <div>Wind socks: <b>{emrg.wind_socks?.count}</b></div>
          <div>Safety showers: <b>{emrg.safety_showers?.count}</b> @ {emrg.safety_showers?.spacing_m} m spacing</div>
          <div>First aid stations: <b>{emrg.first_aid?.stations}</b></div>
          <div>SCBA: <b>{(emrg.rescue_equipment || [])[0]}</b></div>
          <div>Code: <b>{emrg.code_reference}</b></div>
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="h3-schematic-svg-btn"
                  onClick={() => _openSvg(projectId, "/stage-h/fp-schematic/svg", {})}>
          Preview foam schematic SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="h3-schematic-dxf-btn"
                  onClick={() => _downloadDxf(projectId, "/stage-h/fp-schematic/dxf", {},
                    "foam-schematic.dxf")}>
          Download foam schematic DXF
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ H.4 · MTO & Deliverables ══════════════════ */
function H4MTO({ projectId, res }) {
  const mto = res?.fire_mto_summary || {};
  const items = res?.fire_mto_line_items_first10 || [];
  const reqs = res?.requisitions || [];
  const report = res?.fire_safety_report || {};
  const reg = res?.drawing_register || [];
  const comp = res?.completeness || {};
  return (
    <div className="space-y-4" data-testid="h4-mto">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="MTO line items"    value={mto.line_items_count || 0}
                    testid="h4-tile-mto" />
        <StatTile label="Piping (m)"    value={_fmt(mto.totals?.piping_m, 0)}
                    testid="h4-tile-piping" />
        <StatTile label="Detectors"    value={mto.totals?.detectors_count || 0}
                    testid="h4-tile-detectors" />
        <StatTile label="Fireproof (m²)"
                    value={_fmt(mto.totals?.fireproofing_m2, 0)}
                    testid="h4-tile-fp-mto" />
      </div>

      <Section title={`Fire protection MTO — first 10 of ${mto.line_items_count} line items`}
                  testid="h4-mto-table">
        <Table cols={[
          ["item", "Item"], ["qty", "Qty"],
          ["unit", "Unit"], ["category", "Category"],
        ]} rows={items} />
        <div className="mt-2 text-[11px] text-neutral-400">
          Categories: {(mto.by_category_keys || []).join(" · ")}
        </div>
      </Section>

      <Section title="Requisition packages (H.4-F2)" testid="h4-reqs">
        <Table cols={[
          ["package", "Pkg"], ["description", "Description"],
          ["long_lead", "Long lead"],
          ["lead_time_weeks", "Lead (w)"],
          ["vendor_hint", "Vendor hint"],
        ]} rows={reqs} />
      </Section>

      <Section title="Fire safety report (H.4-F3)" testid="h4-report">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Project: <b>{report.project}</b> · Country: <b>{report.country}</b></div>
          <div className="mt-2 space-y-1">
            {(report.sections || []).map(s => (
              <div key={s.no} className="border-l-2 border-neutral-700 pl-2">
                <b>§{s.no} {s.title}</b>: {s.content}
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section title={`Drawing register (${reg.length} drawings)`}
                  testid="h4-drawings">
        <Table cols={[
          ["drawing_no", "Drawing #"],
          ["title", "Title"],
          ["sheets", "Sheets"],
          ["scale", "Scale"],
          ["reference", "Ref"],
        ]} rows={reg} />
      </Section>

      <Section title="Completeness (H.4-F5)" testid="h4-complete">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Status: <b className={comp.status === "PASS" ? "text-green-400" : comp.status === "REVIEW" ? "text-amber-400" : "text-red-400"}>{comp.status}</b> ({comp.percent_complete}%)</div>
          <div>Pass: {comp.count_pass} / {comp.count_total}</div>
          {(comp.missing || []).length > 0 && (
            <div className="text-amber-300">Missing: {(comp.missing || []).slice(0, 5).join(", ")}</div>
          )}
        </div>
      </Section>
    </div>
  );
}

/* ═════════════════════ Wrapper ════════════════════════════════ */
export default function StageHPanel({ projectId, subStage }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/stage-h/run`,
                                       { country: "IN",
                                         plot_size_m: [100, 60] });
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.message });
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="space-y-4" data-testid={`stage-h-panel-${subStage}`}>
      <div className="flex items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 p-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Stage H · Fire Protection</div>
          <div className="text-xs text-neutral-400">
            Sub-stage {subStage} · powered by H.1-H.4 gap-closure engines (25 fixes)
          </div>
        </div>
        <button onClick={run} className="btn btn-primary text-xs"
                  data-testid="stage-h-rerun-btn">Re-run pipeline</button>
      </div>

      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-500"
              data-testid="stage-h-loading">
          Running Stage H pipeline · fire-water scenarios · F&amp;G mapping · deluge/foam/fireproofing · MTO…
        </div>
      )}

      {!loading && res?.error && (
        <div className="rounded-md border border-red-900 bg-red-950/50 p-4 text-xs text-red-300"
              data-testid="stage-h-error">
          Pipeline error: {res.error}
        </div>
      )}

      {!loading && !res?.error && (
        <>
          {subStage === "H.1" && <H1FireWater  projectId={projectId} res={res} />}
          {subStage === "H.2" && <H2Detection  projectId={projectId} res={res} />}
          {subStage === "H.3" && <H3Suppression projectId={projectId} res={res} />}
          {subStage === "H.4" && <H4MTO         projectId={projectId} res={res} />}
        </>
      )}
    </div>
  );
}
